'use client';

import React, { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Avatar,
  Select,
  SelectItem,
  Chip,
} from '@heroui/react';
import { IdView, ResponsiveModal, Typography } from '../../../../components';
import CustomHeroInput from '../../../../components/CustomHeroInput';
import CustomTextarea from '../../../../components/CustomTextarea';
import { useTranslations } from '../../../../contexts/translation.context';
import {
  UserNoAvatarIcon,
  OnlyFansIc,
  InstagramIc,
  GoogleIc,
  TwitterIc,
  IcReddit,
  IcTg,
  IcTikTok,
  IcYoutube,
  IcAnotherSoc,
} from '../../../../components/icons';
import partnerRevshareService, {
  RevshareOrder,
} from '../../../../api/partner/partnerRevshareService';
import { parseApiError } from '../../../../utils/errorParser';
import { showError, showPartnerSuccess } from '../../../../utils/toast';
import { useAuth } from '../../../../contexts/AuthContext';

interface NewCostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const NewCostModal: React.FC<NewCostModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslations();

  // Form state
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Campaigns (orders) state
  const [campaigns, setCampaigns] = useState<RevshareOrder[]>([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);

  // Load campaigns when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCampaigns();
    }
  }, [isOpen]);

  const loadCampaigns = async () => {
    setIsLoadingCampaigns(true);
    try {
      const response = await partnerRevshareService.getOrders({
        page: 1,
        page_size: 50,
        status: 'in_progress',
      });
      setCampaigns(response.objects || []);
    } catch (err) {
      console.error('Error loading campaigns:', err);
    } finally {
      setIsLoadingCampaigns(false);
    }
  };
  const { user } = useAuth();
  const selectedCampaignData = useMemo(() => {
    return campaigns.find((c) => c._id === selectedCampaign);
  }, [selectedCampaign, campaigns]);

  const handleSubmit = async () => {
    if (!selectedCampaignData) {
      showError(t('partner.revshare.costs.modal.selectCampaign'));
      return;
    }

    const numAmount = parseFloat(amount) || 0;
    if (numAmount <= 0) {
      showError(t('partner.revshare.costs.modal.amountRequired') || 'Укажите сумму');
      return;
    }

    setIsSubmitting(true);

    try {
      await partnerRevshareService.createCost({
        amount: numAmount,
        client_id: selectedCampaignData.offer.client.id || user?.id,
        company_id: selectedCampaignData.model?.track_link_id || 0,
        description: notes,
        inner_company_id: selectedCampaignData._id,
      });

      showPartnerSuccess(
        t('partner.revshare.costs.modal.successMessage') || 'Затрата успешно добавлена'
      );
      onSubmit();
      handleReset();
      onClose();
    } catch (err: any) {
      console.error('Error creating cost:', err);
      showError(
        parseApiError(
          err,
          t('partner.revshare.costs.modal.errorMessage') || 'Ошибка при создании затраты'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedCampaign('');
    setAmount('');
    setNotes('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  // Get short ID from _id (last 5 characters)
  const getCampaignShortId = (campaign: RevshareOrder) => {
    const idString = campaign._id.slice(-4);
    return <IdView textToCopy={campaign._id}>{idString}</IdView>;
    //  `#${campaign._id.slice(-4)}`;
  };

  // Get campaign link ending for display (e.g. "c168" from "https://onlyfans.com/yoooourmaid/c168")
  const getCampaignModelName = (campaign: RevshareOrder) => {
    return campaign.model.of_user_id?.toString()?.slice(-4) || '';
    const link = campaign.model?.link;
    if (link) {
      const parts = link.split('/');
      return parts[parts.length - 1] || '';
    }
    return campaign.model?.of_user_name || campaign.model?.name || '';
  };

  // Get source display name
  const getSourceDisplayName = (source: string) => {
    const sourceMap: Record<string, string> = {
      only_fans: 'OnlyFans',
      onlyfans: 'OnlyFans',
      reddit: 'Reddit',
      instagram: 'Instagram',
      tiktok: 'TikTok',
      google: 'Google',
      telegram: 'Telegram',
      twitter: 'Twitter',
      youtube: 'YouTube',
      fansly: 'Fansly',
    };
    return sourceMap[source?.toLowerCase()] || source || '';
  };

  // Get source icon component
  const getSourceIcon = (source: string) => {
    const sourceIcons: Record<string, React.ReactNode> = {
      only_fans: <OnlyFansIc size={14} />,
      onlyfans: <OnlyFansIc size={14} />,
      reddit: <IcReddit size={14} />,
      instagram: <InstagramIc size={14} fill="#11181C" />,
      tiktok: <IcTikTok size={14} />,
      google: <GoogleIc size={14} fill="#11181C" />,
      telegram: <IcTg size={14} />,
      twitter: <TwitterIc size={14} fill="#11181C" />,
      youtube: <IcYoutube size={14} />,
    };
    return sourceIcons[source?.toLowerCase()] || <IcAnotherSoc size={14} />;
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Handle campaign selection change
  const handleCampaignSelectionChange = (keys: any) => {
    if (typeof keys === 'string') {
      setSelectedCampaign(keys);
    } else if (keys instanceof Set) {
      const values = Array.from(keys);
      setSelectedCampaign(values[0]?.toString() || '');
    } else if (keys && keys.currentKey) {
      setSelectedCampaign(keys.currentKey);
    }
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      classNames={{
        base: 'md:max-w-[37.5rem]',
        backdrop: 'bg-black/50',
      }}
    >
      <ModalContent className="rounded-4">
        <ModalHeader className="flex flex-col gap-3 px-4 md:px-6 pt-6 pb-4">
          <Typography variant="h4" className="font-medium text-auth-ink !text-xl">
            {t('partner.revshare.costs.modal.title')}
          </Typography>
        </ModalHeader>

        <ModalBody className="px-4 md:px-6 py-3">
          <div className="flex flex-col gap-4">
            {/* Marketing Campaign */}
            <div className="flex flex-col gap-2">
              <Typography variant="body2" className="text-auth-ink">
                {t('partner.revshare.costs.modal.marketingCampaign')}
              </Typography>
              <Select
                aria-label={t('partner.revshare.costs.modal.marketingCampaign')}
                placeholder={t('partner.revshare.costs.modal.selectCampaign')}
                selectedKeys={selectedCampaign ? [selectedCampaign] : []}
                onSelectionChange={handleCampaignSelectionChange}
                isLoading={isLoadingCampaigns}
                variant="bordered"
                classNames={{
                  trigger:
                    'min-h-[2.375rem] !rounded-2 border-auth-strength-none data-[open=true]:border-neutral-slate',
                  value: 'text-sm',
                  popoverContent: '!rounded-3 p-2 shadow-[0px_0px_24px_0px_rgba(0,0,0,0.1)]',
                  listbox: 'p-0 gap-0',
                }}
                renderValue={(items) => {
                  if (!selectedCampaignData) return null;
                  return (
                    <div className="flex items-center gap-2">
                      <Avatar
                        src={selectedCampaignData.model?.avatar}
                        fallback={<UserNoAvatarIcon />}
                        className="w-6 h-6 rounded-[0.3125rem] flex-shrink-0"
                      />
                      <span className="text-sm text-auth-ink">
                        {getCampaignShortId(selectedCampaignData)}
                      </span>
                      <Typography variant="label3" className="text-xs font-semibold text-auth-ink">
                        {getCampaignModelName(selectedCampaignData)}
                      </Typography>
                      <Typography variant="body4" className="text-xxs !text-auth-muted">
                        от {formatDate(selectedCampaignData.created_at)}
                      </Typography>
                      <Chip
                        size="sm"
                        variant="bordered"
                        classNames={{
                          base: 'h-6 border-[1.4px] border-auth-strength-none rounded-3.5 px-1',
                          content: 'flex items-center gap-0.5 px-1 text-xs text-auth-ink',
                        }}
                        startContent={getSourceIcon(selectedCampaignData.source)}
                      >
                        {getSourceDisplayName(selectedCampaignData.source)}{' '}
                        {formatDate(selectedCampaignData.created_at)}
                      </Chip>
                    </div>
                  );
                }}
              >
                {campaigns.map((campaign) => (
                  <SelectItem
                    key={campaign._id}
                    textValue={`#${campaign._id.slice(-4)} ${getCampaignModelName(campaign)}`}
                    classNames={{
                      base: 'px-2 py-1.5 rounded-3 data-[hover=true]:bg-transparent data-[selectable=true]:focus:bg-transparent',
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Avatar
                        src={campaign.model?.avatar}
                        fallback={<UserNoAvatarIcon />}
                        className="w-6 h-6 rounded-[0.3125rem] flex-shrink-0"
                      />
                      <div className="flex items-center gap-2 flex-1 flex-wrap">
                        <span className="text-sm text-auth-ink">
                          {getCampaignShortId(campaign)}
                        </span>
                        <span className="text-xs font-semibold text-auth-ink">
                          {getCampaignModelName(campaign)}
                        </span>
                        <span className="text-xxs text-auth-muted">
                          от {formatDate(campaign.created_at)}
                        </span>
                        <Chip
                          size="sm"
                          variant="bordered"
                          classNames={{
                            base: 'h-6 border-[1.4px] border-auth-strength-none rounded-3.5 px-1',
                            content: 'flex items-center gap-0.5 px-1 text-xs text-auth-ink',
                          }}
                          startContent={getSourceIcon(campaign.source)}
                        >
                          {getSourceDisplayName(campaign.source)} {formatDate(campaign.created_at)}
                        </Chip>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </Select>
            </div>

            {/* Amount and Transaction ID */}
            <div className="flex gap-4">
              <div className="flex-1">
                <CustomHeroInput
                  label={t('partner.revshare.costs.modal.amount')}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  type="number"
                />
              </div>
              {/* <div className="flex-[2]">
                <CustomHeroInput
                  label="ID транзакции или ссылка"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('partner.revshare.costs.modal.enterData')}
                />
              </div> */}
            </div>

            {/* Notes */}
            <CustomTextarea
              label={t('partner.revshare.costs.modal.notes')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder=""
              minRows={4}
              maxRows={6}
            />
          </div>
        </ModalBody>

        <ModalFooter className="px-6 pb-4 pt-0 h-[4.5rem] flex items-center justify-end">
          <div className="flex gap-2">
            <Button
              className="bg-[rgba(243,18,96,0.2)] h-10 px-4 rounded-3 min-w-[5.625rem]"
              onPress={handleClose}
              isDisabled={isSubmitting}
            >
              <Typography variant="body2" className="!text-auth-strength-weak font-normal">
                {t('partner.revshare.costs.modal.close')}
              </Typography>
            </Button>
            <Button
              className="bg-partner h-10 px-3.5 py-2.5 rounded-3 min-w-[6.25rem]"
              onPress={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
            >
              <Typography variant="body2" className="!text-white font-semibold">
                {t('partner.revshare.costs.modal.addCost')}
              </Typography>
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default NewCostModal;
